'use client'

import { useState } from 'react'
import Button from '@/components/ui/Button'

interface MembershipRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  clubName: string
  registrationUrl?: string | null
  loading?: boolean
}

export default function MembershipRequestModal({
  isOpen,
  onClose,
  onConfirm,
  clubName,
  registrationUrl,
  loading = false
}: MembershipRequestModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-ocean-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">
                Request to Join {clubName}
              </h3>
            </div>
          </div>

          <div className="mb-6">
            {registrationUrl ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800">
                        Official Registration Required
                      </h4>
                      <div className="mt-2 text-sm text-blue-700">
                        <p className="mb-3">
                          Before requesting to join {clubName} in the app, you must first register with the club through their official registration process.
                        </p>
                        <div className="bg-white rounded border p-3">
                          <p className="font-medium text-blue-900 mb-2">Official Registration:</p>
                          <a
                            href={registrationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline break-all"
                          >
                            {registrationUrl}
                          </a>
                        </div>
                        <p className="mt-3 text-xs">
                          Click the link above to open the registration page in a new tab.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-yellow-800">
                        Confirmation Required
                      </h4>
                      <p className="mt-1 text-sm text-yellow-700">
                        Please confirm that you have already completed the official registration process before proceeding with your app membership request.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">
                      Membership Confirmation
                    </h4>
                    <p className="mt-1 text-sm text-yellow-700">
                      Please confirm that you are already a paid member of {clubName}. Club membership is handled outside of this app, and you should only request app membership if you have already joined the club officially.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Requesting...' : 'Yes, I\'m a Member - Request Access'}
            </Button>
            <Button
              onClick={onClose}
              variant="secondary"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>

          {registrationUrl && (
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Need to register first?{' '}
                <a
                  href={registrationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ocean-600 hover:text-ocean-800 underline"
                >
                  Visit registration page
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
